import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { GetterService } from './getter-service.service';
import { Theme, ThemeColors, ThemeRadius, WebCustomer } from 'src/Model/WebCustomer';
import {
  DEFAULT_THEME_PRESET,
  FALLBACK_THEME_PRESET,
  THEME_PRESETS,
  ThemePreset,
} from '../theme/theme-presets';

/**
 * Turns `TemplateData.Theme` into the `--ui-*` custom properties declared in
 * src/_ui-tokens.scss, set on <html> once the WebCustomer payload resolves.
 *
 * Every redesigned surface (new-header, new-menu, recipe-option-modal,
 * new-checkout, new-side-cart, new-recomendations, rewards, gift-cards)
 * reads only those properties, so this is the single place where colour
 * enters those screens. No `color-mix()` — the mixes are precomputed here so
 * old Android webviews stay happy.
 *
 * Resolution order (later wins):
 *   stylesheet defaults → preset (`Theme.Preset`) → `Theme.Colors` overrides
 *   → derived tokens computed from whatever core colours resulted.
 *
 * When `Theme` is absent nothing is written and the stylesheet defaults
 * apply, so existing brands are unaffected.
 */

type Rgb = [number, number, number];

/** Stylesheet defaults, kept in sync with the :root block of _ui-tokens.scss. */
const BASE: ThemeColors = {
  Accent: '#e11d62',
  AccentInk: '#ffffff',
  Ground: '#ffffff',
  Card: '#ffffff',
  Ink: '#1b1b1b',
  Line: '#dfe1e6',
  Success: '#1a7a4b',
  Danger: '#e7004b',
  Price: '#e13d3d',
  HeaderBg: '',
  HeaderInk: '',
  BannerBg: '#111111',
  BannerText: '#ffffff',

  AccentHi: '',
  AccentHover: '',
  AccentRing: '',
  AccentTint: '',
  AccentBadge: '',
  Ink2: '',
  InkMuted: '',
  InkFaint: '',
  LineStrong: '',
  LineInput: '',
  Hairline: '',
  SuccessTint: '',
  SuccessBadge: '',
  DangerTint: '',
  DangerNote: '',
  HeaderLine: '',
  HeaderHover: '',
  BannerTextSoft: '',
  BannerTextMuted: '',
  ThumbBg: '',
};

/** ThemeColors key → CSS custom property. */
const TOKEN: Record<keyof ThemeColors, string> = {
  Accent: '--ui-accent',
  AccentInk: '--ui-accent-ink',
  AccentHi: '--ui-accent-hi',
  AccentHover: '--ui-accent-hover',
  AccentRing: '--ui-accent-ring',
  AccentTint: '--ui-accent-tint',
  AccentBadge: '--ui-accent-badge',
  Ground: '--ui-ground',
  Card: '--ui-card',
  Ink: '--ui-ink',
  Ink2: '--ui-ink-2',
  InkMuted: '--ui-ink-muted',
  InkFaint: '--ui-ink-faint',
  Line: '--ui-line',
  LineStrong: '--ui-line-strong',
  LineInput: '--ui-line-input',
  Hairline: '--ui-hairline',
  Success: '--ui-success',
  SuccessTint: '--ui-success-tint',
  SuccessBadge: '--ui-success-badge',
  Danger: '--ui-danger',
  DangerTint: '--ui-danger-tint',
  DangerNote: '--ui-danger-note',
  Price: '--ui-price',
  HeaderBg: '--ui-header-bg',
  HeaderInk: '--ui-header-ink',
  HeaderLine: '--ui-header-line',
  HeaderHover: '--ui-header-hover',
  BannerBg: '--ui-banner-bg',
  BannerText: '--ui-banner-text',
  BannerTextSoft: '--ui-banner-text-soft',
  BannerTextMuted: '--ui-banner-text-muted',
  ThumbBg: '--ui-thumb-bg',
};

const RADIUS_TOKEN: Record<keyof ThemeRadius, string> = {
  Modal: '--ui-r-modal',
  Panel: '--ui-r-panel',
  Card: '--ui-r-card',
  Choice: '--ui-r-choice',
  Mini: '--ui-r-mini',
  Thumb: '--ui-r-thumb',
};

/** Tokens that are not part of ThemeColors but follow from it. */
const EXTRA_TOKENS = ['--ui-foot-bg', '--ui-foot-line', '--ui-scrim'];

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  /** The fully resolved palette last applied (null until a Theme is applied). */
  current: ThemeColors | null = null;

  constructor(private apiService: ApiService, private local: GetterService) {
    this.apiService.getWebcustomer$.subscribe((res) => {
      if (!res) return;
      this.applyFromWebCustomer(res);
    });
  }

  applyFromWebCustomer(res: WebCustomer): void {
    // No Theme block is not "no theme": it means the brand has not chosen a
    // palette, so DEFAULT_THEME_PRESET decides. That is `brand`, which lifts
    // the accent from the colours they already set under
    // DynamicStyle.SecondaryColors — previously this bailed out and left the
    // raspberry values baked into _ui-tokens.scss showing on every brand.
    const theme = res.TemplateData?.Theme || ({} as Theme);

    const preset = this.resolvePreset(theme, res);
    this.apply(preset, theme.Colors, theme.Radius);
    this.saveSplash(res);
  }

  /**
   * Remember what the boot splash should look like on this brand's next
   * visit. index.html cannot wait for the WebCustomer call, so its inline
   * script reads this record (key "<brandFlavor> splash", the GetterService
   * namespace) before Angular starts. Plain hex only — the script validates
   * every colour with /^#[0-9a-f]{6}$/ and ignores anything else.
   */
  private saveSplash(res: WebCustomer): void {
    const c = this.current;
    if (!c) return;
    const ground = parse(c.Ground) || parse(BASE.Ground);
    const accent = parse(c.Accent) || parse(BASE.Accent);
    const header = parse(c.HeaderBg) || parse(c.Card) || ground;
    const record = {
      v: 1,
      logo: res.BrandData?.BrandBranch?.BrandBranchLogo || '',
      name: res.BrandData?.BrandName || '',
      ground: hex(ground),
      ink: hex(parse(c.Ink) || parse(BASE.Ink)),
      accent: hex(accent),
      track: hex(mix(accent, ground, 0.16)),
      bar: hex(header),
    };
    try {
      this.local.set('splash', JSON.stringify(record));
    } catch {
      /* storage full or blocked — next visit just gets the neutral splash */
    }
  }

  /**
   * Apply a palette directly (used by the WebCustomer hook; also handy from
   * the console: `ng.getInjector(...)` is overkill — just call this).
   */
  apply(preset: ThemePreset = {}, overrides: Partial<ThemeColors> = {}, radius?: ThemeRadius): void {
    const explicit: Partial<ThemeColors> = { ...preset, ...overrides };
    const colors = this.resolve(explicit);
    const root = document.documentElement.style;

    (Object.keys(TOKEN) as (keyof ThemeColors)[]).forEach((key) => {
      const value = colors[key];
      if (value) root.setProperty(TOKEN[key], value);
    });

    const ground = parse(colors.Ground);
    const ink = parse(colors.Ink);
    if (ground) root.setProperty('--ui-foot-bg', rgba(ground, 0.96));
    /* Light or dark page: lets component stylesheets that carry deliberate
       literal colours (the dish swatches) switch to a dark variant via
       :host-context([data-ui-scheme="dark"]). Light presets never set it.
       color-scheme makes the browser's own parts (scrollbars, native
       selects, the canvas under the page) dark too. */
    const html = document.documentElement;
    if (ground && luminance(ground) < 0.2) {
      html.setAttribute('data-ui-scheme', 'dark');
      html.style.setProperty('color-scheme', 'dark');
    } else {
      html.removeAttribute('data-ui-scheme');
      html.style.removeProperty('color-scheme');
    }
    if (ink) root.setProperty('--ui-foot-line', rgba(ink, 0.04));
    if (ink) root.setProperty('--ui-scrim', rgba(mix(ink, [0, 0, 0], 0.6), 0.16));

    (Object.keys(RADIUS_TOKEN) as (keyof ThemeRadius)[]).forEach((key) => {
      const value = radius?.[key];
      if (value) root.setProperty(RADIUS_TOKEN[key], value);
      else root.removeProperty(RADIUS_TOKEN[key]);
    });

    this.current = colors;
    this.setBrowserBar(colors);
  }

  /** Phone browser bar (meta theme-color) follows the header surface. */
  private setBrowserBar(colors: ThemeColors): void {
    const bar = parse(colors.HeaderBg) || parse(colors.Card);
    if (!bar) return;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = hex(bar);
  }

  /**
   * Read a resolved `--ui-*` token off <html>, for third-party SDKs that need
   * literal colours (Stripe Elements, Square card, Paymentsense Connect-E).
   * `fallback` is the stylesheet default, used before the first paint or in
   * environments without getComputedStyle.
   */
  token(name: string, fallback: string = ''): string {
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue(name);
      return (v && v.trim()) || fallback;
    } catch {
      return fallback;
    }
  }

  /**
   * Legacy colour fallback for widgets shared with the old screens (payment
   * forms render inside the legacy checkout, pay, reservation and events
   * overlays too). With no `Theme` configured those brands keep their
   * `DynamicStyle` button colours: the returned map scopes the accent tokens
   * to the widget's root via `[ngStyle]`, and the derived hover/ring/tint
   * follow. With a `Theme` it returns null and the page tokens apply.
   */
  legacyAccentVars(res: WebCustomer): Record<string, string> | null {
    if (!res || res.TemplateData?.Theme) return null;
    const secondary = res.TemplateData?.DynamicStyle?.SecondaryColors;
    const accent = parse(secondary?.SelectedButtonColor);
    if (!accent) return null;
    const card = parse(BASE.Card);
    const ink = parse(BASE.Ink);
    const accentInk = parse(secondary?.SelectedButtonTextColor);
    return {
      '--ui-accent': hex(accent),
      '--ui-accent-ink': accentInk ? hex(accentInk) : contrastInk(accent, ink),
      '--ui-accent-hi': hex(mix(accent, card, 0.78)),
      '--ui-accent-hover': hex(mix(accent, ink, 0.88)),
      '--ui-accent-ring': hex(mix(accent, card, 0.62)),
      '--ui-accent-tint': hex(mix(accent, card, 0.07)),
      '--ui-accent-badge': hex(mix(accent, card, 0.85)),
    };
  }

  /** Remove every property this service may have set; stylesheet defaults return. */
  clear(): void {
    const root = document.documentElement.style;
    Object.values(TOKEN).forEach((t) => root.removeProperty(t));
    Object.values(RADIUS_TOKEN).forEach((t) => root.removeProperty(t));
    EXTRA_TOKENS.forEach((t) => root.removeProperty(t));
    document.documentElement.removeAttribute('data-ui-scheme');
    root.removeProperty('color-scheme');
    this.current = null;
  }

  private resolvePreset(theme: Theme, res: WebCustomer): ThemePreset {
    const name = (theme.Preset || DEFAULT_THEME_PRESET).toLowerCase();

    const presets = THEME_PRESETS as Record<string, ThemePreset>;

    if (name === 'brand') {
      const secondary = res.TemplateData?.DynamicStyle?.SecondaryColors;
      const preset: ThemePreset = {};
      if (secondary?.SelectedButtonColor) preset.Accent = secondary.SelectedButtonColor;
      if (secondary?.SelectedButtonTextColor) preset.AccentInk = secondary.SelectedButtonTextColor;

      // brand is the default now, so it has to cope with a brand that has
      // nothing to lift — an empty preset would leave the accent unset
      return preset.Accent ? preset : presets[FALLBACK_THEME_PRESET];
    }

    return presets[name] || presets[FALLBACK_THEME_PRESET];
  }

  /**
   * Fill in every key of ThemeColors: explicit values win, then derived
   * values are computed from the core colours, then stylesheet defaults.
   */
  private resolve(explicit: Partial<ThemeColors>): ThemeColors {
    const given = stripEmpty(explicit);
    const c: ThemeColors = { ...BASE, ...given };

    const card = parse(c.Card) || parse(BASE.Card);
    const ink = parse(c.Ink) || parse(BASE.Ink);
    const accent = parse(c.Accent) || parse(BASE.Accent);
    const line = parse(c.Line) || parse(BASE.Line);
    const success = parse(c.Success) || parse(BASE.Success);
    const danger = parse(c.Danger) || parse(BASE.Danger);
    const bannerBg = parse(c.BannerBg) || parse(BASE.BannerBg);
    const bannerText = parse(c.BannerText) || parse(BASE.BannerText);

    const set = (key: keyof ThemeColors, value: string) => {
      if (!given[key]) c[key] = value;
    };


    /* accent family — mixed towards the card surface so it works on dark cards too */
    set('AccentInk', contrastInk(accent, ink));
    set('AccentHi', hex(mix(accent, card, 0.78)));
    set('AccentHover', hex(mix(accent, ink, 0.88)));
    set('AccentRing', hex(mix(accent, card, 0.62)));
    set('AccentTint', hex(mix(accent, card, 0.07)));
    set('AccentBadge', hex(mix(accent, card, 0.85)));

    /* ink family */
    set('Ink2', hex(mix(ink, card, 0.87)));
    set('InkMuted', hex(mix(ink, card, 0.66)));
    set('InkFaint', hex(mix(ink, card, 0.34)));

    /* lines */
    set('LineStrong', hex(mix(line, ink, 0.95)));
    set('LineInput', hex(mix(line, ink, 0.93)));
    set('Hairline', hex(mix(line, card, 0.6)));
    set('ThumbBg', hex(mix(card, ink, 0.94)));

    /* semantic */
    set('SuccessTint', hex(mix(success, card, 0.08)));
    // default badge green pairs with the default Success above; a brand that
    // sets its own Success gets that colour for fills too
    set('SuccessBadge', given.Success ? hex(success) : '#50cd89');
    set('DangerTint', hex(mix(danger, card, 0.06)));
    set('DangerNote', hex(mix(danger, ink, 0.85)));

    /* header defaults to the card surface; hover is a faint wash of its ink */
    set('HeaderBg', c.Card);
    const headerBg = parse(c.HeaderBg) || card;
    const headerIsCard = hex(headerBg) === hex(card);
    set('HeaderInk', headerIsCard ? c.Ink : contrastInk(headerBg, ink));
    const headerInk = parse(c.HeaderInk) || ink;
    set('HeaderLine', headerIsCard ? c.Line : hex(mix(headerInk, headerBg, 0.18)));
    // on a card-coloured header the hover wash is the ground, unless the ground
    // is the same surface (white on white) — then a faint ink wash instead
    const groundRgb = parse(c.Ground) || card;
    const groundIsCard = hex(groundRgb) === hex(card);
    set(
      'HeaderHover',
      headerIsCard
        ? groundIsCard
          ? hex(mix(ink, card, 0.05))
          : c.Ground
        : hex(mix(headerInk, headerBg, 0.1))
    );

    /* banner (dark photo panels) */
    set('BannerTextSoft', hex(mix(bannerText, bannerBg, 0.9)));
    set('BannerTextMuted', hex(mix(bannerText, bannerBg, 0.72)));

    return c;
  }
}

/* ---------------------------------------------------------------------------
   Colour helpers — tiny on purpose; no dependency.
   ------------------------------------------------------------------------- */

function stripEmpty<T extends object>(o: T): Partial<T> {
  const out: Partial<T> = {};
  Object.keys(o).forEach((k) => {
    const v = (o as any)[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') (out as any)[k] = String(v).trim();
  });
  return out;
}

/** Parses #rgb, #rgba, #rrggbb, #rrggbbaa, rgb(), rgba(). Returns null otherwise. */
export function parse(input: string | undefined): Rgb | null {
  if (!input) return null;
  const s = input.trim().toLowerCase();

  if (s[0] === '#') {
    const h = s.slice(1);
    if (h.length === 3 || h.length === 4) {
      return [h[0] + h[0], h[1] + h[1], h[2] + h[2]].map((x) => parseInt(x, 16)) as Rgb;
    }
    if (h.length === 6 || h.length === 8) {
      return [h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)].map((x) => parseInt(x, 16)) as Rgb;
    }
    return null;
  }

  const m = s.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])].map(clamp255) as Rgb;

  return NAMED[s] ? parse(NAMED[s]) : null;
}

/** `weight` is the share of `a`; 1 → a, 0 → b. */
export function mix(a: Rgb, b: Rgb, weight: number): Rgb {
  const w = Math.max(0, Math.min(1, weight));
  return [0, 1, 2].map((i) => Math.round(a[i] * w + b[i] * (1 - w))) as Rgb;
}

export function hex([r, g, b]: Rgb): string {
  return '#' + [r, g, b].map((x) => clamp255(x).toString(16).padStart(2, '0')).join('');
}

export function rgba([r, g, b]: Rgb, alpha: number): string {
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** WCAG relative luminance. */
export function luminance([r, g, b]: Rgb): number {
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function contrast(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** White or the theme ink, whichever reads better on `bg`. */
function contrastInk(bg: Rgb, ink: Rgb): string {
  const white: Rgb = [255, 255, 255];
  const dark: Rgb = luminance(ink) < 0.5 ? ink : [27, 27, 27];
  return contrast(bg, white) >= contrast(bg, dark) ? '#ffffff' : hex(dark);
}

function clamp255(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)));
}

/** The handful of CSS names a brand config is likely to contain. */
const NAMED: Record<string, string> = {
  white: '#ffffff',
  black: '#000000',
  red: '#ff0000',
  green: '#008000',
  blue: '#0000ff',
  navy: '#000080',
  teal: '#008080',
  purple: '#800080',
  orange: '#ffa500',
  gold: '#ffd700',
  gray: '#808080',
  grey: '#808080',
  silver: '#c0c0c0',
  maroon: '#800000',
  olive: '#808000',
  crimson: '#dc143c',
  coral: '#ff7f50',
  tomato: '#ff6347',
  salmon: '#fa8072',
  pink: '#ffc0cb',
  hotpink: '#ff69b4',
  deeppink: '#ff1493',
};
